import { useState, useRef, useEffect } from 'react';
import { Send, CheckCircle, Mail } from 'lucide-react';
import FadeIn from './FadeIn';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';
const tertiary = '#9A9690';
const borderRest = '#E5E3DE';

const inputBase = {
  width: '100%',
  padding: '12px 0',
  background: 'transparent',
  border: 'none',
  borderBottom: `1px solid ${borderRest}`,
  color: charcoal,
  fontSize: '1rem',
  fontWeight: 300,
  lineHeight: '1.6',
  outline: 'none',
  caretColor: gold,
  transition: 'border-color 0.25s',
};

const GoldButton = ({ children, type = 'button', disabled, onClick, className = '' }) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    className={`w-full py-4 text-sm font-medium uppercase flex items-center justify-center gap-3 transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed ${className}`}
    style={{ border: `1px solid ${gold}`, color: gold, letterSpacing: '0.2em', backgroundColor: 'transparent' }}
    onMouseEnter={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = gold; e.currentTarget.style.color = '#fff'; } }}
    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = gold; }}
  >
    {children}
  </button>
);

const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => o === value);

  return (
    <div ref={ref} className="relative" style={{ borderBottom: `1px solid ${open ? gold : borderRest}`, transition: 'border-color 0.25s' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3 bg-transparent text-base font-light focus:outline-none"
        style={{ color: selected ? charcoal : tertiary, letterSpacing: '0.01em' }}
      >
        <span>{selected || placeholder}</span>
        <svg width="11" height="6" viewBox="0 0 11 6" fill="none" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <path d="M1 1L5.5 5L10 1" stroke={gold} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 bg-white" style={{ border: `1px solid ${borderRest}`, borderTop: 'none' }}>
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm font-light transition-colors duration-150"
              style={{ color: opt === value ? gold : secondary, backgroundColor: 'transparent', lineHeight: '1.6', borderBottom: `1px solid #F5F3EF` }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FAFAF8'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >{opt}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactPage = ({ language = 'en' }) => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const subjectOptions = language === 'es' ? [
    'Oportunidades de Desarrollo Inmobiliario',
    'Alianzas de Capital e Inversión',
    'Invitados al Podcast y Colaboraciones',
    'Conversaciones Estratégicas sobre IA, Sostenibilidad y Desarrollo',
  ] : [
    'Real estate development opportunities',
    'Capital partnerships and investment',
    'Podcast guests and collaborations',
    'Strategic conversations around AI, sustainability, and development',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/mdklooag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => { setIsSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }, 5000);
      } else {
        alert('Error sending message. Please try again.');
      }
    } catch {
      alert('Error sending message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (subscribeEmail) {
      setIsSubscribed(true);
      setTimeout(() => { setIsSubscribed(false); setSubscribeEmail(''); }, 5000);
    }
  };

  const labelStyle = { color: tertiary, letterSpacing: '0.15em', textTransform: 'uppercase' };

  return (
    <div className="min-h-screen bg-white">
      <div style={{ paddingTop: 'env(safe-area-inset-top)', position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#fff' }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="px-6 text-center" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-light mb-5" style={{ color: charcoal, letterSpacing: '-0.02em', lineHeight: '1.1' }}>
          {language === 'es' ? 'Contacto' : 'Contact'}
        </h1>
        <p className="text-lg sm:text-xl font-light max-w-md mx-auto mb-6" style={{ color: secondary, lineHeight: '1.8' }}>
          {language === 'es' ? 'Conéctate, colabora o mantente alineado.' : 'Connect, collaborate, or stay aligned.'}
        </p>
        <div className="w-8 h-px mx-auto mb-10" style={{ backgroundColor: gold }} />
        <p className="text-sm sm:text-base font-light max-w-xl mx-auto" style={{ color: tertiary, lineHeight: '2' }}>
          {language === 'es'
            ? 'Esta plataforma está construida alrededor del pensamiento a largo plazo, la ejecución disciplinada y la colaboración significativa. Ya sea que estés construyendo, invirtiendo o explorando ideas en la intersección de activos reales, capital e innovación — aquí comienza la alineación.'
            : 'This platform is built around long-term thinking, disciplined execution, and meaningful collaboration. Whether you are building, investing, or exploring ideas at the intersection of real asset, capital, and innovation — this is where alignment begins.'}
        </p>
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — SUBSCRIBE ────────────────────────────── */}
      <section className="contact-screen-section px-6">
        <FadeIn>
          <div className="max-w-lg mx-auto text-center">
            <p className="text-xs font-medium mb-4" style={{ color: gold, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {language === 'es' ? 'Boletín' : 'Newsletter'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={{ color: charcoal, lineHeight: '1.3' }}>
              {language === 'es' ? 'Mantente Conectado' : 'Stay Connected'}
            </h2>
            <p className="text-sm font-light mb-3" style={{ color: secondary, lineHeight: '1.8' }}>
              {language === 'es' ? 'Una carta semanal sobre bienes raíces, capital y sistemas de largo horizonte.' : 'A weekly letter on real estate, capital, and long-horizon systems.'}
            </p>
            <p className="text-sm font-light mb-10" style={{ color: tertiary, lineHeight: '1.9' }}>
              {language === 'es'
                ? 'Perspectivas sobre desarrollo, estrategia de inversión, sostenibilidad y el futuro del entorno construido — escrito para operadores, inversores y constructores.'
                : 'Insights on development, investment strategy, sustainability, and the future of the built environment — written for operators, investors, and builders.'}
            </p>
            {!isSubscribed ? (
              <form onSubmit={handleSubscribe} className="space-y-4 text-left">
                <input
                  type="email" value={subscribeEmail} onChange={e => setSubscribeEmail(e.target.value)}
                  placeholder={language === 'es' ? 'Tu correo electrónico' : 'Enter your email'}
                  style={inputBase}
                  onFocus={e => (e.target.style.borderBottomColor = gold)}
                  onBlur={e => (e.target.style.borderBottomColor = borderRest)}
                  required
                />
                <div className="pt-3">
                  <GoldButton type="submit">{language === 'es' ? 'Suscribirse' : 'Subscribe'}</GoldButton>
                </div>
              </form>
            ) : (
              <div className="py-10">
                <div className="w-10 h-10 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ border: `1px solid ${gold}` }}>
                  <CheckCircle className="w-4 h-4" style={{ color: gold }} />
                </div>
                <p className="text-sm font-light" style={{ color: secondary, lineHeight: '2' }}>
                  {language === 'es' ? 'Suscrito. Nos vemos en tu bandeja.' : 'Subscribed. See you in your inbox.'}
                </p>
              </div>
            )}
          </div>
        </FadeIn>
      </section>

      <div className="max-w-lg mx-auto px-6"><div style={{ height: '1px', backgroundColor: '#F0EDE8' }} /></div>

      {/* ─── SECTION 2 — CONTACT FORM ─────────────────────────── */}
      <section className="contact-screen-section px-6">
        <FadeIn>
          <div className="max-w-5xl mx-auto grid gap-8 lg:gap-12 lg:grid-cols-[0.88fr_1fr] lg:items-start">
            <div className="relative overflow-hidden bg-black h-[360px] max-w-sm mx-auto sm:h-[440px] lg:h-auto lg:min-h-[540px] lg:max-w-none lg:sticky lg:top-28" style={{ borderRadius: '18px' }}>
              <img src="/image1.webp" alt={language === 'es' ? 'Retrato profesional de Zeus Hernandez' : 'Professional portrait of Zeus Hernandez'} className="h-full w-full object-cover" style={{ objectPosition: '50% 28%', filter: 'contrast(1.04)' }} />
            </div>
            <div className="max-w-lg mx-auto lg:max-w-none lg:pt-2">
              <div className="text-center lg:text-left mb-9">
                <p className="text-xs font-medium mb-4" style={{ color: gold, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                  {language === 'es' ? 'Ponte en Contacto' : 'Get in Touch'}
                </p>
                <h2 className="text-3xl sm:text-4xl font-light mb-4" style={{ color: charcoal, lineHeight: '1.3' }}>
                  {language === 'es' ? 'Escríbenos' : 'Reach Out'}
                </h2>
                <p className="text-sm font-light" style={{ color: secondary, lineHeight: '1.9' }}>
                  {language === 'es' ? 'Para conversaciones reflexivas, alianzas u oportunidades alineadas con la plataforma.' : 'For thoughtful conversations, partnerships, or opportunities aligned with the platform.'}
                </p>
              </div>
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-7">
                  <div>
                    <label className="block text-xs font-medium mb-3" style={labelStyle}>{language === 'es' ? 'Nombre' : 'Name'}</label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={language === 'es' ? 'Tu nombre' : 'Your name'} style={inputBase} onFocus={e => (e.target.style.borderBottomColor = gold)} onBlur={e => (e.target.style.borderBottomColor = borderRest)} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-3" style={labelStyle}>{language === 'es' ? 'Correo' : 'Email'}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={language === 'es' ? 'tu@correo.com' : 'your@email.com'} style={inputBase} onFocus={e => (e.target.style.borderBottomColor = gold)} onBlur={e => (e.target.style.borderBottomColor = borderRest)} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-3" style={labelStyle}>{language === 'es' ? 'Área de Interés' : 'Area of Interest'}</label>
                    <CustomSelect value={formData.subject} onChange={val => setFormData(prev => ({ ...prev, subject: val }))} options={subjectOptions} placeholder={language === 'es' ? 'Selecciona un área...' : 'Select an area...'} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-3" style={labelStyle}>{language === 'es' ? 'Mensaje' : 'Message'}</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} placeholder={language === 'es' ? 'Cuéntame sobre tu proyecto o idea...' : 'Tell me about your project or idea...'} rows="5" style={{ ...inputBase, resize: 'none', lineHeight: '1.8' }} onFocus={e => (e.target.style.borderBottomColor = gold)} onBlur={e => (e.target.style.borderBottomColor = borderRest)} required />
                  </div>
                  <div className="pt-2">
                    <GoldButton type="submit" disabled={isSubmitting}>
                      <Send className={`w-4 h-4 ${isSubmitting ? 'animate-pulse' : ''}`} />
                      {isSubmitting ? (language === 'es' ? 'Enviando...' : 'Sending...') : (language === 'es' ? 'Enviar Mensaje' : 'Send Message')}
                    </GoldButton>
                  </div>
                </form>
              ) : (
                <div className="text-center py-16">
                  <div className="w-10 h-10 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ border: `1px solid ${gold}` }}>
                    <CheckCircle className="w-4 h-4" style={{ color: gold }} />
                  </div>
                  <h3 className="text-xl font-light mb-3" style={{ color: charcoal }}>{language === 'es' ? 'Mensaje enviado.' : 'Message sent.'}</h3>
                  <p className="text-sm font-light max-w-xs mx-auto" style={{ color: secondary, lineHeight: '2' }}>
                    {language === 'es' ? 'Gracias por ponerte en contacto. Continuaremos la conversación pronto.' : "Thanks for reaching out. We'll continue the conversation soon."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </FadeIn>
      </section>

      <div className="max-w-lg mx-auto px-6"><div style={{ height: '1px', backgroundColor: '#F0EDE8' }} /></div>

      {/* ─── SECTION 3 — COLLABORATION ────────────────────────── */}
      <section className="contact-screen-section px-6">
        <FadeIn>
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-medium mb-4" style={{ color: gold, letterSpacing: '0.2em', textTransform: 'uppercase' }}>{language === 'es' ? 'Colaboración' : 'Collaboration'}</p>
              <h2 className="text-3xl sm:text-4xl font-light mb-6" style={{ color: charcoal, lineHeight: '1.3' }}>{language === 'es' ? 'Colaboración' : 'Collaboration'}</h2>
            </div>
            <p className="text-sm font-light mb-6 text-center" style={{ color: secondary, lineHeight: '2' }}>
              {language === 'es' ? 'Esta plataforma está construyendo activamente en:' : 'This platform is actively building across:'}
            </p>
            <ul className="space-y-3 mb-8 flex flex-col items-center text-center">
              {(language === 'es' ? [
                'Desarrollo inmobiliario (Infinity Development)',
                'Plataformas de capital e inversión',
                'IA y sistemas dentro del entorno construido',
                'Infraestructura e innovación de largo horizonte',
              ] : [
                'real estate development (Infinity Development)',
                'capital and investment platforms',
                'AI and systems within the built environment',
                'long-horizon infrastructure and innovation',
              ]).map((item, i) => (
                <li key={i} className="inline-flex items-start justify-center gap-3 max-w-md">
                  <span className="mt-3 w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: gold }} />
                  <span className="text-sm font-light" style={{ color: secondary, lineHeight: '1.9' }}>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm font-light mb-3 text-center" style={{ color: secondary, lineHeight: '2' }}>
              {language === 'es' ? 'Si tu trabajo se alinea con estas áreas, puede haber oportunidades de colaborar.' : 'If your work aligns with these areas, there may be opportunities to collaborate.'}
            </p>
            <p className="text-sm font-light mb-1 text-center" style={{ color: charcoal, lineHeight: '2' }}>{language === 'es' ? 'Esto no es abierto.' : 'This is not open-ended.'}</p>
            <p className="text-sm font-light text-center" style={{ color: charcoal, lineHeight: '2' }}>{language === 'es' ? 'Es intencional.' : 'It is intentional.'}</p>
          </div>
        </FadeIn>
      </section>

      <div className="max-w-lg mx-auto px-6"><div style={{ height: '1px', backgroundColor: '#F0EDE8' }} /></div>

      {/* ─── SECTION 4 — DIRECT CHANNELS ─────────────────────── */}
      <section className="contact-screen-section px-6">
        <FadeIn>
          <div className="max-w-lg mx-auto text-center">
            <p className="text-xs font-medium mb-4" style={{ color: gold, letterSpacing: '0.2em', textTransform: 'uppercase' }}>{language === 'es' ? 'Directo' : 'Direct'}</p>
            <h2 className="text-3xl sm:text-4xl font-light mb-3" style={{ color: charcoal, lineHeight: '1.3' }}>{language === 'es' ? 'Canales Directos' : 'Direct'}</h2>
            <p className="text-sm font-light mb-10" style={{ color: secondary, lineHeight: '1.9' }}>
              {language === 'es' ? 'Para comunicación directa o actualizaciones de la plataforma:' : 'For direct communication or platform updates:'}
            </p>
            <div className="flex items-center justify-center gap-7">
              {[
                { href: 'https://www.linkedin.com/in/zeus-hernandez/', label: 'LinkedIn', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg> },
                { href: 'https://www.instagram.com/thezeushernandez/', label: 'Instagram', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> },
                { href: 'https://www.youtube.com/@thezeushernandez', label: 'YouTube', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/></svg> },
                { href: 'https://zeushernandez.substack.com/', label: 'Newsletter', icon: <Mail className="w-4 h-4" style={{ color: 'inherit' }} /> },
              ].map(({ href, label, icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2" aria-label={label}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300" style={{ border: `1px solid ${borderRest}`, color: '#111111' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = gold; e.currentTarget.style.color = gold; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = borderRest; e.currentTarget.style.color = '#111111'; }}
                  >{icon}</div>
                  <span className="text-xs font-light" style={{ color: tertiary, letterSpacing: '0.08em' }}>{label}</span>
                </a>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      <div className="max-w-lg mx-auto px-6"><div style={{ height: '1px', backgroundColor: '#F0EDE8' }} /></div>

      {/* ─── SECTION 5 — CLOSING ──────────────────────────────── */}
      <section className="contact-screen-section px-6 text-center">
        <FadeIn>
          <div className="max-w-md mx-auto">
            <p className="text-xs font-medium mb-8" style={{ color: gold, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {language === 'es' ? 'Pensamiento Final' : 'Closing Thought'}
            </p>
            <p className="text-xl sm:text-2xl font-light mb-5" style={{ color: charcoal, lineHeight: '1.7' }}>
              {language === 'es' ? 'Esta plataforma está construida para quienes piensan en décadas, no en trimestres.' : 'This platform is built for those who think in decades, not quarters.'}
            </p>
            <p className="text-sm font-light" style={{ color: secondary, lineHeight: '2' }}>
              {language === 'es' ? 'Si eso resuena — deberíamos estar en conversación.' : 'If that resonates — we should be in conversation.'}
            </p>
            <div className="w-8 h-px mx-auto mt-10" style={{ backgroundColor: gold }} />
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default ContactPage;
